import { Component, inject } from '@angular/core';
import { ButtonsModule, CheckboxModule, ComponentsModule, DialogModule, DialogService, FormFieldModule, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { RegionService } from '@lib/region/region.service';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { PrioritySingleResponse, StorePriorityRequest, createPriorityFormGroup, Priority } from '@lib/api/models/priorities';
import { NgFor } from '@angular/common';

export interface RegionData {
    id: string;
    name: string;
    priorities: Priority[];
}

@Component({
    selector: 'app-add-region-priority-dialog',
    imports: [
        ButtonsModule,
        CheckboxModule,
        DialogModule,
        ErrorPanelComponent,
        ReactiveFormsModule,
        FormFieldModule,
        FormsModule,
        ComponentsModule,
        NgFor
    ],
    templateUrl: './add-region-priority-dialog.component.html',
    standalone: true,
    styleUrl: './add-region-priority-dialog.component.scss'
})

export class AddRegionPriorityDialogComponent {

    private readonly _regionService = inject(RegionService);
    private readonly _snackBar = inject(SnackBarService);

    protected readonly data = inject<RegionData>(DIALOG_DATA);
    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    protected readonly priorityFormGroup = createPriorityFormGroup();

    addPriorityRequest = new SendableRequest<PrioritySingleResponse, StorePriorityRequest>({
        request: payload => this._regionService.createPriority(this.data.id, payload!),

        onSuccess: () => {
            this._snackBar.open({
                colour: 'success',
                dismissable: false,
                icon: 'check_circle',
                title: 'Priority created successfully',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });

            this.dialogRef.close();
        }
    });

    add() {

        if (this.priorityFormGroup.invalid) {
            this._snackBar.open({
                colour: 'error',
                dismissable: false,
                icon: 'error_outline',
                title: 'Please fill in all required fields',
                actions: [
                    { id: 'undo', label: 'OK' }
                ]
            });
            return;
        }

        const priorityData = {
            'name': this.priorityFormGroup.value.name ?? '',
            'description': this.priorityFormGroup.value.description ?? '',
            'delay': this.priorityFormGroup.value.delay ?? 0,
            'status': true
        };

        this.addPriorityRequest.send(priorityData);
    }

    public static show(dialog: DialogService, data: RegionData) {
        return dialog.openDialog<DialogRef, RegionData>(AddRegionPriorityDialogComponent, {
            data
        });
    }
}
